import React, { useState } from "react";
import { Select, Button, Modal, Checkbox, Radio } from "antd";
import { TagOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import Axios from "axios";
import { antdAsyncMessageSuccess, antdAsyncMessageError, antdAsyncMessageLoading } from "../../helpers/AsyncMessage";

const ImumBulkTagAction = ({
    visible,
    onClose,
    checkedProductIds,
    imumProductTags,
    onSuccess,
    filterString,
    totalFilteredCount,
}) => {
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [useFilter, setUseFilter] = useState(false);

    console.log('ImumBulkTagAction props:', {
        totalFilteredCount,
        filterString,
        checkedProductIds: checkedProductIds.length
    });

    const handleBulkTagAction = async (action) => {
        if (!selectedTagIds || selectedTagIds.length === 0) {
            antdAsyncMessageError({
                key: 'bulk_tag_error',
                content: 'Please select at least one tag',
            });
            return;
        }

        if (!useFilter && checkedProductIds.length === 0) {
            antdAsyncMessageError({
                key: 'bulk_tag_error',
                content: 'No products selected',
            });
            return;
        }

        if (useFilter && !filterString) {
            antdAsyncMessageError({
                key: 'bulk_tag_error',
                content: 'No filter applied',
            });
            return;
        }

        const key = `bulk_tag_${action}`;
        setLoading(true);

        try {
            const productCount = useFilter ? totalFilteredCount : checkedProductIds.length;

            antdAsyncMessageLoading({
                key,
                content: `${action === 'add' ? 'Adding' : 'Removing'} ${selectedTagIds.length} tag(s) for ${productCount} products...`,
            });

            const requestData = useFilter
                ? {
                    filter: filterString,
                    tag_id: selectedTagIds,
                    tag_action: action,
                }
                : {
                    product_ids: checkedProductIds,
                    tag_id: selectedTagIds,
                    tag_action: action,
                };

            const { data } = await Axios.post(
                `/api/v1/${window.identifier}/imums/bulk_tag_action`,
                requestData
            );

            if (data.success) {
                antdAsyncMessageSuccess({
                    key,
                    content: `Successfully ${action === 'add' ? 'added' : 'removed'} ${selectedTagIds.length} tag(s) for ${data.updated_count || productCount} products`,
                });

                setSelectedTagIds([]);
                setUseFilter(false);
                if (onSuccess) onSuccess();
                if (onClose) onClose();
            } else {
                throw new Error(data.message || "Bulk tag action failed");
            }
        } catch (error) {
            console.error("Bulk tag action error:", error);
            antdAsyncMessageError({
                key,
                content: error.response?.data?.message || error.message || "Failed to update tags",
            });
        } finally {
            setLoading(false);
        }
    };

    const tagOptions = (imumProductTags || []).map((tag) => ({
        value: tag.value,
        label: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                    style={{
                        width: 12,
                        height: 12,
                        backgroundColor: tag.color || '#ccc',
                        borderRadius: 2,
                        marginRight: 8,
                    }}
                />
                <span>{tag.label}</span>
            </div>
        ),
    }));

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TagOutlined style={{ fontSize: 18, color: '#1890ff', marginRight: 8 }} />
                    <span>Bulk Tag Action</span>
                </div>
            }
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={500}
        >
            <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Apply to:
                </label>
                <Radio.Group
                    value={useFilter}
                    onChange={(e) => setUseFilter(e.target.value)}
                    style={{ width: '100%', marginBottom: 16 }}
                >
                    <Radio value={false} style={{ display: 'block', marginBottom: 8 }}>
                        Selected products only ({checkedProductIds.length} selected)
                    </Radio>
                    <Radio value={true} style={{ display: 'block' }}>
                        All filtered products ({totalFilteredCount || 0} total)
                        {!filterString && <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>(no filter applied)</span>}
                    </Radio>
                </Radio.Group>
            </div>

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Select Tags:
                </label>
                <Select
                    mode="multiple"
                    placeholder="Select one or more tags"
                    style={{ width: '100%' }}
                    value={selectedTagIds}
                    onChange={setSelectedTagIds}
                    options={tagOptions}
                    showSearch
                    filterOption={(input, option) =>
                        option.label.props.children[1].props.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
                <Button
                    type="primary"
                    onClick={() => handleBulkTagAction('add')}
                    loading={loading}
                    disabled={
                        !selectedTagIds ||
                        selectedTagIds.length === 0 ||
                        (!useFilter && checkedProductIds.length === 0) ||
                        (useFilter && !filterString)
                    }
                >
                    Add Tag
                </Button>

                <Button
                    danger
                    onClick={() => handleBulkTagAction('remove')}
                    loading={loading}
                    disabled={
                        !selectedTagIds ||
                        selectedTagIds.length === 0 ||
                        (!useFilter && checkedProductIds.length === 0) ||
                        (useFilter && !filterString)
                    }
                >
                    Remove Tag
                </Button>
            </div>
        </Modal>
    );
};

export default ImumBulkTagAction;
